import React, { createContext, useState, useCallback, useMemo, useEffect } from 'react';
import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import type { User, AuthState } from '@/types/api.types';

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'us-west-2_9r8e9zsqV',
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '7gfbdba8qck91se32hm8v4uth3',
  Storage: window.localStorage,
};
const userPool = new CognitoUserPool(poolData);

interface AuthContextValue extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  loginError: string | null;
}

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  token: null,
  login: async () => {},
  logout: () => {},
  refreshToken: async () => {},
  loginError: null,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    token: null,
  });
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.getSession((err: any, session: any) => {
        if (err || !session || !session.isValid()) {
          setState({ user: null, isAuthenticated: false, isLoading: false, token: null });
          return;
        }
        const idToken = session.getIdToken();
        const payload = idToken.decodePayload();
        setState({
          user: {
            id: payload.sub,
            email: payload.email || payload['cognito:username'] || '',
            name: payload.name || payload['cognito:username'] || 'User',
            company: payload['custom:company'] || 'ClaimsIQ',
            role: payload['custom:role'] || 'CLIENT_USER',
            plan: 'PROFESSIONAL' as const,
            createdAt: new Date().toISOString(),
          },
          isAuthenticated: true,
          isLoading: false,
          token: idToken.getJwtToken(),
        });
      });
    } else {
      setState({ user: null, isAuthenticated: false, isLoading: false, token: null });
    }
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    setLoginError(null);
    setState((prev) => ({ ...prev, isLoading: true }));

    return new Promise<void>((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
      const authDetails = new AuthenticationDetails({ Username: username, Password: password });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          const idToken = session.getIdToken();
          const payload = idToken.decodePayload();
          setState({
            user: {
              id: payload.sub,
              email: payload.email || username,
              name: payload.name || username,
              company: payload['custom:company'] || 'ClaimsIQ',
              role: payload['custom:role'] || 'CLIENT_USER',
              plan: 'PROFESSIONAL' as const,
              createdAt: new Date().toISOString(),
            },
            isAuthenticated: true,
            isLoading: false,
            token: idToken.getJwtToken(),
          });
          resolve();
        },
        onFailure: (err) => {
          const msg = err.message || 'Authentication failed';
          setLoginError(msg);
          setState({ user: null, isAuthenticated: false, isLoading: false, token: null });
          reject(new Error(msg));
        },
        newPasswordRequired: (userAttributes) => {
          setLoginError('Password change required. Please contact admin.');
          setState((prev) => ({ ...prev, isLoading: false }));
          reject(new Error('NEW_PASSWORD_REQUIRED'));
        },
      });
    });
  }, []);

  const logout = useCallback(() => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) currentUser.signOut();
    setState({ user: null, isAuthenticated: false, isLoading: false, token: null });
    setLoginError(null);
  }, []);

  const refreshToken = useCallback(async () => {
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) return;
    currentUser.getSession((err: any, session: any) => {
      if (!err && session && session.isValid()) {
        setState((prev) => ({ ...prev, token: session.getIdToken().getJwtToken() }));
      }
    });
  }, []);

  const value = useMemo(
    () => ({ ...state, login, logout, refreshToken, loginError }),
    [state, login, logout, refreshToken, loginError]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};